import {
  ICulture,
  ICultureVariation,
} from 'app/containers/Cultures/slice/types';
import { ITaskCategory } from 'app/containers/Task/slice/types';

/* --- STATE --- */
export interface ResourceState {
  harvest: {
    loading: boolean;
    harvestArr: IHarvestListItem[];
    selectedCultureId: null | number;
  };

  harvestByCultureAndCompany: {
    loading: boolean;
    harvestByCultureAndCompanyArr: IHarvestByCultureAndCompanyListItem[];
  };

  resourceTypes: {
    loading: boolean;
    typesArr: IResourceType[];
    selectedResourceTypeId: number | null;
  };

  resources: {
    loading: boolean;
    resourcesArr: IResource[];
    selectedResourceId: number | null;
    availableResourcesArr: IAvailableResource[];
  };

  resourceAllocation: {
    changesSubmitLoading: boolean;
    selectedTaskCategoryId: number | null;
    selectedUsedResourceId: number | null;
    resourcesByTaskCategory: {
      resourcesArr: IResourceByTaskCategory[];
      loading: boolean;
    };
    resourceDistribution: {
      resourcesArr: IResourceDistributionListItem[];
      loading: boolean;
    };
  };
}

export interface IGetHarvestPayload {
  envId: number;
  companyId: number | null;
  companyGroupId: number | null;
}

export interface IGetHarvestByCultureAndCompanyPayload {
  envId: number;
  cultureId: number;
  companyId: number | null;
  companyGroupId: number | null;
}

export interface IHarvestByCultureAndCompanyListItem {
  id: string;
  name: string;
  company: {
    id: number;
    name: string;
    companyCode: string;
    city: string;
    accepted: null;
  };
  culture: ICulture;
  harvest: number;
}

export interface IHarvestListItem {
  name: string;
  culture: ICulture;
  harvest: number;
}

export interface IResourceType {
  id: number;
  name: string;
  unit: string;
  seed: boolean;
  yield: boolean;
}

export interface IGetResourcesByTaskCategoryIdPayload {
  envId: number;
  taskCategoryId: number;
  companyId: number | null;
  companyGroupId: number | null;
  filters: IWarehouseFilters;
  seasonId: number;
}

export interface IGetUsedResourceDistributionPayload {
  seasonId: number;
  envId: number;
  taskCategoryId: number;
  companyId: number | null;
  companyGroupId: number | null;
  filters: IWarehouseFilters;
  resourceId: number;
}

export interface IAvailableResource {
  id: number;
  name: string;
  type: {
    id: number;
    name: string;
    unit: string;
    seed: boolean;
    yield: boolean;
  };
  cultureVariation: ICultureVariation;
  recommendedNorm: number;
}

export interface IResource {
  name: string;
  resource: IAvailableResource;
  totalUnits: number;
  unitsPlanned: number;
  unitsUsed: number;
  unitsLeft: number;
  futureUnitsLeft: number;
  writeOff: number;
  type: IResourceType;
}

export interface IResourceByTaskCategory {
  name: string;
  resource: {
    id: number;
    name: string;
    countryCode: string;
    type: { id: number; name: string; unit: string };
    recommendedNorm: number;
  };
  amount: number;
}

export interface IResourceDistributionListItem {
  id: number;
  company: {
    id: number;
    name: string;
    companyCode: string;
    city: string;
    accepted: boolean;
  };
  category: ITaskCategory;
  resource: {
    id: number;
    name: string;
    countryCode: string;
    type: {
      id: number;
      name: string;
      unit: string;
      seed: boolean;
    };
    recommendedNorm: number;
  };
  culture: {
    id: number;
    name: string;
    code: string;
    countryCode: string;
    color: string;
  } | null;
  fieldSeason: {
    id: number;
    season: {
      id: number;
      year: number;
      startDate: string;
      endDate: string;
      name: string;
    };
    culture: {
      id: number;
      name: string;
      code: string;
      countryCode: string;
      color: string;
    };
    field: string;
    geometry: string;
    area: number;
  };
  name: string | null;
  amount: number;
  startDate: string;
  endDate: string;
  fieldTask: {
    id: number;
    taskId: number;
    fieldSeason: {
      id: number;
      season: {
        id: number;
        year: number;
        startDate: string;
        endDate: string;
        name: string;
      };
      culture: {
        id: number;
        name: string;
        code: string;
        countryCode: string;
        color: string;
      };
      field: string;
      geometry: string;
      area: number;
    };
    progress: number;
    status: string;
    startDate: string;
    endDate: string;
    createdBy: {
      id: number;
      name: string;
      lastName: string;
      email: string;
      phoneNr: string;
      companyCount: number;
    };
    updatedBy: {
      id: number;
      name: string;
      lastName: string;
      email: string;
      phoneNr: string;
      companyCount: number;
    };
    createdAt: string;
    updatedAt: string;
  };
}

export interface IWarehouseFilters {
  startDate: string | null;
  endDate: string | null;
  search: string;
  cultureId: number | null;
}

export enum WarehouseFiltersEnum {
  cultureId = 'cultureId',
  search = 'search',
  startDate = 'startDate',
  endDate = 'endDate',
  date = 'date',
}

export interface IUsedResource {
  amount: number;
  fieldTaskId: number;
  resourceId: number;
}

export interface ISubmitUsedResourcesUpdatesPayload {
  envId: number;
  usedResourcesArr: IUsedResource[];
  filters: IWarehouseFilters;
}
interface IUsedResourceData {
  id: number;
  name: string;
  countryCode: string;
  type: {
    id: number;
    name: string;
    unit: string;
    seed: boolean;
    yield: boolean;
  };
  description: string | null;
  recommendedNorm: number;
  cultureVariation: null | ICultureVariation;
  n: number | null;
  p: number | null;
  k: number | null;
  s: number | null;
}
export interface IUsedResourceByResourceType {
  resource: IUsedResourceData;
  resourceType: IResourceType;
  totalAmount: number;
  priceStatus: {
    usedResourcesCount: number;
    pricesCount: number;
    allPricesPresent: boolean | null;
    averagePrice: number | null;
  };
}

export interface IUsedResourcePrice {
  averagePrice: number | null;
  resourceId: number;
  companyName: string;
  companyId: number;
  amountUsed: number;
  usedResourcePriceId: number | null;
}
export interface IUsedResourcePricesResponse {
  resource: IUsedResourceData;
  usedResourcePriceList: IUsedResourcePrice[];
}

export interface IUsedResourcePriceCreateViewItem {
  averagePrice: number;
  companyId: number;
  hiikoEnvironmentId: number;
  id: number | null;
  resourceId: number;
  seasonId: number;
}
